import { useRef, useEffect, useMemo, useState } from "react";
import { useFeedbackStore, type FeedbackItem } from "../../stores/feedback.js";
import { FeedbackMessage } from "./FeedbackMessage.js";

const TYPE_COLORS: Record<string, string> = {
  progress: "bg-brand-cyan",
  completion: "bg-brand-green",
  error: "bg-brand-pink",
  decision: "bg-brand-purple",
  observation: "bg-brand-cyan/60",
  artifact: "bg-yellow-400",
  "request-for-input": "bg-amber-400",
  confidence: "bg-text-muted",
};

const BACKEND_COLORS: Record<string, string> = {
  "claude-agent-sdk": "border-l-brand-purple",
  "openai-agents": "border-l-brand-green",
  "cloudflare-agents": "border-l-brand-pink",
  "raw-api": "border-l-brand-cyan",
};

interface FeedbackSwimlaneProps {
  filters: Set<string>;
  search: string;
}

export function FeedbackSwimlane({ filters, search }: FeedbackSwimlaneProps) {
  const messagesByAgent = useFeedbackStore((s) => s.messagesByAgent);
  const messages = useFeedbackStore((s) => s.messages);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [selectedMsg, setSelectedMsg] = useState<FeedbackItem | null>(null);

  // Auto-scroll right
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = scrollRef.current.scrollWidth;
    }
  }, [messages.length]);

  // Compute time range
  const timeRange = useMemo(() => {
    if (messages.length === 0) return { start: Date.now(), end: Date.now(), duration: 1 };
    const timestamps = messages.map((m) => new Date(m.timestamp).getTime());
    const start = Math.min(...timestamps);
    const end = Math.max(...timestamps);
    const duration = Math.max(end - start, 1000); // minimum 1 second
    return { start, end, duration };
  }, [messages]);

  // Get agents sorted by first activity
  const agents = useMemo(() => {
    const agentEntries: Array<{ id: string; name: string; messages: FeedbackItem[] }> = [];
    for (const [agentId, msgs] of messagesByAgent) {
      const filtered = msgs.filter((m) => {
        if (filters.size > 0 && !filters.has(m.type)) return false;
        if (search) {
          const content = JSON.stringify(m.payload).toLowerCase();
          if (!content.includes(search.toLowerCase())) return false;
        }
        return true;
      });
      if (filtered.length > 0) {
        agentEntries.push({
          id: agentId,
          name: msgs[0]?.source?.name ?? agentId,
          messages: filtered,
        });
      }
    }
    return agentEntries.sort(
      (a, b) =>
        new Date(a.messages[0]?.timestamp ?? 0).getTime() -
        new Date(b.messages[0]?.timestamp ?? 0).getTime(),
    );
  }, [messagesByAgent, filters, search]);

  if (agents.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-sm text-text-muted">
        Activity lanes will appear when agents start reporting
      </div>
    );
  }

  const LANE_HEIGHT = 36;
  const timelineWidth = Math.max(600, messages.length * 20);

  return (
    <div className="h-full flex flex-col">
      <div ref={scrollRef} className="flex-1 overflow-auto">
        <div style={{ minWidth: timelineWidth }} className="relative">
          {/* Time axis header */}
          <div className="sticky top-0 z-10 h-6 bg-surface/90 backdrop-blur-sm border-b border-border-subtle/15 flex items-center pl-28">
            <TimeAxis
              start={timeRange.start}
              end={timeRange.end}
              width={timelineWidth - 112}
            />
          </div>

          {/* Swim lanes */}
          {agents.map((agent) => (
            <div
              key={agent.id}
              className="flex border-b border-border-subtle/10 hover:bg-surface-lighter/5 transition-colors"
              style={{ height: LANE_HEIGHT }}
            >
              {/* Agent label */}
              <div className="w-28 shrink-0 flex items-center px-3 border-r border-border-subtle/10 sticky left-0 bg-surface/80 backdrop-blur-sm z-[5]">
                <span className="text-[11px] text-text-secondary truncate font-medium">
                  {agent.name}
                </span>
              </div>

              {/* Message blocks */}
              <div className="flex-1 relative">
                {agent.messages.map((msg) => {
                  const ts = new Date(msg.timestamp).getTime();
                  const position =
                    ((ts - timeRange.start) / timeRange.duration) * 100;
                  const color = TYPE_COLORS[msg.type] ?? "bg-text-muted";

                  return (
                    <button
                      key={msg.id}
                      onClick={() => setSelectedMsg(selectedMsg?.id === msg.id ? null : msg)}
                      className={`absolute top-1/2 -translate-y-1/2 h-4 rounded-sm transition-[transform,box-shadow] duration-150 hover:scale-y-150 hover:z-10 ${color} ${
                        selectedMsg?.id === msg.id ? "ring-1 ring-white/40 z-10" : ""
                      }`}
                      style={{
                        left: `${position}%`,
                        width: "8px",
                        minWidth: "4px",
                        opacity: 0.8,
                      }}
                      title={`${msg.type}: ${extractBrief(msg.payload)}`}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Selected message detail */}
      {selectedMsg && (
        <div className="border-t border-border-subtle/15 max-h-32 overflow-y-auto">
          <FeedbackMessage msg={selectedMsg} />
        </div>
      )}
    </div>
  );
}

function TimeAxis({
  start,
  end,
  width,
}: {
  start: number;
  end: number;
  width: number;
}) {
  const duration = end - start;
  const tickCount = Math.min(8, Math.max(3, Math.floor(width / 100)));
  const ticks = [];

  for (let i = 0; i <= tickCount; i++) {
    const ts = start + (duration * i) / tickCount;
    const time = new Date(ts).toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    const position = (i / tickCount) * 100;
    ticks.push(
      <span
        key={i}
        className="absolute text-[9px] text-text-muted font-mono -translate-x-1/2 tabular-nums"
        style={{ left: `${position}%` }}
      >
        {time}
      </span>,
    );
  }

  return <div className="relative flex-1 h-full">{ticks}</div>;
}

function extractBrief(payload: any): string {
  return (
    payload.message ??
    payload.summary ??
    payload.decision ??
    payload.observation ??
    payload.question ??
    payload.name ??
    ""
  ).slice(0, 60);
}
